<template>
    <authenticated-layout>
        <v-card class="px-3">
            <v-card-title
                class="d-flex flex-col md:flex-row justify-space-between align-center pa-2"
            >
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-4 w-full lg:w-[600px]">
                    <div class="cols-spam ">
                        <v-select
                            v-model="form.salesperson"
                            :label="$t('customers.guardian')"
                            placeholder="Select a guardian"
                            :items="[
                                {
                                    title: $t('customers.everybody'),
                                    value: null,
                                },
                                ...salespeople.map((o) => ({
                                    title: o.name,
                                    value: o.pd_id,
                                })),
                            ]"
                            variant="outlined"
                            density="compact"
                            persistent-placeholder
                            hide-details
                        />
                    </div>

                    <div class="d-flex align-center col-span-2">
                        <v-text-field
                            v-model="form.search"
                            label="Search"
                            placeholder="Type to search..."
                            variant="outlined"
                            density="compact"
                            persistent-placeholder
                            hide-details
                        />

                        <v-btn
                            variant="flat"
                            class="ml-2"
                            size="small"
                            color="blue"
                            @click="reset()"
                        >
                            {{ $t("sharedSearchFilter.Reset") }}
                        </v-btn>
                    </div>
                </div>

                <div class="mt-4 md:mt-0 md:ml-2">
                    <span class="text-sm mr-1" :style="`color: ${pipedriveSyncInfo['color']}`">{{ pipedriveSyncInfo['text'] }}</span>

                    <v-btn
                        variant="flat"
                        href="/customers/sync"
                        size="small"
                        color="secondary"
                    >
                        {{ $t("customers.syncPD") }}
                    </v-btn>

                    <div class="hidden">
                        <v-btn
                            v-if="godmode"
                            variant="flat"
                            class="ml-2"
                            size="small"
                            color="primary"
                            @click="openAddCustomerModal()"
                        >
                            {{ $t("customers.Add customer") }}
                        </v-btn>
                    </div>
                </div>
            </v-card-title>

            <div class="overflow-x-auto">
                <table>
                    <tr>
                        <th
                            v-for="(header, index) in headers"
                            :key="`table-header-${index}`"
                            class="text-left whitespace-nowrap"
                        >
                            {{ header }}
                        </th>
                    </tr>

                    <tr
                        v-for="customer in customers.data"
                        :key="`customer-${customer.id}`"
                    >
                        <td>{{ customer.id }}</td>
                        <td>
                            <inertia-link :href="`/customers/${customer.id}/`">
                                {{ customer.name }}
                            </inertia-link>
                        </td>
                        <td>
                            <div
                                class="d-flex justify-space-between align-center"
                            >
                                <span v-if="customer.salesperson_name !== null">
                                    {{ customer.salesperson_name }}
                                </span>

                                <div class="text-right">
                                    <v-btn
                                        v-if="
                                            userRole === 'admin' ||
                                            userRole === 'manager'
                                        "
                                        size="x-small"
                                        class="ml-1"
                                        variant="flat"
                                        color="primary"
                                        @click="onEditOwnerPdId(customer.id)"
                                    >
                                        Edit
                                    </v-btn>

                                    <v-btn
                                        v-if="
                                            userRole === 'admin' ||
                                            userRole === 'manager'
                                        "
                                        size="x-small"
                                        class="ml-1"
                                        variant="flat"
                                        color="secondary"
                                        @click="
                                            syncCustomerOwnerWithPipedrive(
                                                customer.id
                                            )
                                        "
                                    >
                                        Sync
                                    </v-btn>
                                </div>
                            </div>
                        </td>
                        <td>
                            <div
                                class="d-flex align-center"
                            >
                                <v-btn
                                    v-if="heatPumpsEnabled"
                                    size="x-small"
                                    class="mr-1"
                                    variant="outlined"
                                    :href="
                                        customer.questionnaire_present
                                            ? `/customers/${customer.id}/heat-pump-offer/create`
                                            : `/customers/${customer.id}/questionnaire/create`
                                    "
                                    :color="
                                        customer.hp_offers.length > 0
                                            ? 'blue'
                                            : !customer.questionnaire_present
                                            ? 'red'
                                            : ''
                                    "
                                >
                                    {{ $t("customers.heatPumps") }}:
                                    {{ customer.hp_offers.length }}
                                </v-btn>

                                <v-btn
                                    size="x-small"
                                    class="mr-1"
                                    variant="outlined"
                                    :href="`/offers/create/${customer.id}`"
                                    :color="
                                        customer.sp_offers.length > 0
                                            ? 'blue'
                                            : 'red'
                                    "
                                >
                                    {{ $t("customers.photovoltaics") }}:
                                    {{ customer.sp_offers.length }}
                                </v-btn>
                            </div>
                        </td>
                        <td class="text-right">
                            <v-btn
                                size="x-small"
                                variant="flat"
                                color="blue"
                                :href="`/customers/${customer.id}/`"
                            >
                                {{ $t("customers.view") }}
                            </v-btn>
                        </td>
                    </tr>
                </table>
            </div>

            <v-dialog v-model="addCustomerModal" max-width="500" persistent>
                <v-card>
                    <v-card-title class="text-subtitle-1">
                        {{ $t("customers.Add customer") }}
                    </v-card-title>
                    <v-divider />
                    <v-card-text style="max-height: 600px">
                        <v-text-field
                            v-model="addCustomerForm.name"
                            label="Name"
                            variant="outlined"
                            density="compact"
                            type="input"
                        />
                        <v-text-field
                            v-model="addCustomerForm.email"
                            label="Email"
                            variant="outlined"
                            density="compact"
                            type="input"
                        />
                        <v-text-field
                            v-model="addCustomerForm.phone"
                            label="Phone"
                            variant="outlined"
                            density="compact"
                            type="input"
                        />
                        <v-text-field
                            v-model="addCustomerForm.address"
                            label="Address"
                            variant="outlined"
                            density="compact"
                            type="input"
                        />
                    </v-card-text>
                    <v-divider />
                    <v-card-actions>
                        <v-spacer />
                        <v-btn
                            variant="flat"
                            size="small"
                            @click="closeAddCustomerModal()"
                        >
                            Cancel
                        </v-btn>
                        <v-btn
                            color="secondary"
                            variant="flat"
                            size="small"
                            @click="onAddCustomer()"
                        >
                            Add
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-dialog v-model="isModalOpen" max-width="400" persistent>
                <v-card>
                    <v-card-title class="text-subtitle-1">
                        Owner pipedrive ID
                    </v-card-title>

                    <v-divider />

                    <v-card-text>
                        <v-select
                            v-model="ownerPdId"
                            :items="pdOwnersList"
                            label="Select owner pipedrive ID"
                            variant="outlined"
                            density="compact"
                        />
                    </v-card-text>

                    <v-divider />

                    <v-card-actions>
                        <v-spacer />

                        <v-btn
                            variant="flat"
                            size="small"
                            @click="onCloseModal()"
                        >
                            Cancel
                        </v-btn>

                        <v-btn
                            color="secondary"
                            variant="flat"
                            size="small"
                            @click="onUpdateOwnerPdId()"
                        >
                            Update
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <div v-if="customers.links.length > 3" class="mt-6">
                <template v-for="(link, k) in customers.links" :key="k">
                    <v-btn
                        v-if="link.url === null"
                        size="small"
                        variant="flat"
                        class="mr-1"
                    >
                        {{ link.label }}
                    </v-btn>

                    <v-btn
                        v-else
                        :href="link.url"
                        size="small"
                        variant="flat"
                        class="mr-1"
                        :color="link.active ? 'primary' : ''"
                    >
                        {{ link.label }}
                    </v-btn>
                </template>
            </div>
        </v-card>
    </authenticated-layout>
</template>

<script>
import AuthenticatedLayout from "@/Layouts/Authenticated";
import pickBy from "lodash/pickBy";
import throttle from "lodash/throttle";
import mapValues from "lodash/mapValues";

export default {
    components: {
        AuthenticatedLayout,
    },
    props: {
        filters: Object,
        customers: {
            type: Object,
            required: true,
        },
        salespeople: {
            type: Array,
            required: true,
        },
        selectedSalesperson: {
            type: Number,
            required: false,
        },
        userPdId: {
            type: Number,
            required: true,
        },
        userRole: {
            type: String,
            required: true,
        },
        pipedriveIdList: {
            type: Array,
            required: true,
            default: () => [],
        },
        pipedriveSyncInfo: {
            type: Array,
        },
    },
    data() {
        return {
            heatPumpsEnabled: process.env.MIX_HEAT_PUMPS_ENABLED === "true",
            godmode: this.$page.props.auth.user.role !== "sales",
            headers: [
                "ID",
                this.$t("customers.nameSurname"),
                this.$t("customers.guardian"),
                this.$t("customers.offers"),
                "",
            ],
            form: {
                search: this.filters.search,
                salesperson: this.filters.salesperson,
            },
            isModalOpen: false,
            customerId: "",
            ownerPdId: "",
            addCustomerModal: false,
            addCustomerForm: {
                name: null,
                email: null,
                phone: null,
                address: null,
            },
        };
    },
    computed: {
        pdOwnersList() {
            return this.pipedriveIdList.map((o) => {
                return {
                    title: o.name,
                    value: o.pd_id,
                };
            });
        },
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get("/customers", pickBy(this.form), {
                    preserveState: true,
                });
            }, 150),
        },
    },
    methods: {
        onCloseModal() {
            this.isModalOpen = false;
            this.customerId = "";
            this.ownerPdId = "";
        },
        onEditOwnerPdId(customerId) {
            this.customerId = customerId;
            this.isModalOpen = true;
        },
        onUpdateOwnerPdId() {
            if (this.ownerPdId === "") {
                return;
            }

            this.$inertia.patch(
                `/customers/${this.customerId}/update-owner-pd-id`,
                {
                    id: this.customerId,
                    owner_pd_id: this.ownerPdId,
                }
            );

            this.onCloseModal();
        },
        syncCustomerOwnerWithPipedrive(customerId) {
            this.$inertia.post("/sync-customer-owner-with-pipedrive", {
                customerId: customerId,
            });
        },
        reset() {
            this.form = mapValues(this.form, () => null);
        },
        closeAddCustomerModal() {
            for (let x in this.addCustomerForm) {
                this.addCustomerForm[x] = null;
            }
            this.addCustomerModal = false;
        },
        openAddCustomerModal() {
            this.addCustomerModal = true;
        },
        onAddCustomer() {
            this.$inertia.post("/customers", this.addCustomerForm);
            this.closeAddCustomerModal();
        },
    },
};
</script>

<style scoped>
table {
    width: 100%;
    table-layout: fixed;
    font-size: 12px;
}

@media screen and (max-width: 1024px) {
    table {
        width: auto;
    }
}

th {
    padding: 10px 0px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    text-align: left;
    box-sizing: border-box;
    padding-left: 10px;
    border-bottom: 1px solid #aaa;
}

td {
    text-align: left;
    box-sizing: border-box;
    padding: 5px 0px;
    padding-left: 10px;
    border-bottom: 1px solid #ccc;
}
</style>
